// „Приключи без фактура" — за една стокова (от документа) или за избраните от
// доставчик (началният екран, отметки по стокова). Причина по избор и отметка
// „и я отбележи като платена" с полетата на плащането — без нея неплатената
// остава в „Плащане на доставки". Платена стокова не се плаща пак (BE).
// antd Modal: Esc отказва, Ctrl/Cmd+Enter потвърждава.
import { useEffect, useState } from "react";
import { Modal } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import type { Delivery } from "../../../../api/inventory";
import type { CloseWithoutInvoicePayload } from "../../../../api/inventoryDeliveries";
import { modalStyles } from "../../../../utils/modalStyles";
import { useInventoryShell } from "../../components/shell/InventoryShellContext";
import { CheckBox } from "../../components/filters/CheckBox";
import { PRIMARY_BUTTON_CLASS, SECONDARY_BUTTON_CLASS } from "../../components/list/buttonStyles";
import { formatMoney } from "../../components/numbers";
import { PaymentFields } from "../../payments/components/PaymentFields";
import {
  PAYMENT_LABEL_CLASS,
  defaultPaymentFields,
  type PaymentFieldsValue,
} from "../../payments/components/paymentFieldsModel";
import { DeliveryPaymentPill } from "./DeliveryBadges";
import { deliveryGrossOf } from "./deliveryMeta";

type Props = {
  open: boolean;
  notes: Delivery[];
  saving: boolean;
  onCancel: () => void;
  onConfirm: (payload: CloseWithoutInvoicePayload) => void;
  onPickNone: () => void;
};

export const CloseWithoutInvoiceModal = ({ open, notes, saving, onCancel, onConfirm, onPickNone }: Props) => {
  const { t } = useTranslation();
  const { isNarrow } = useInventoryShell();
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [reason, setReason] = useState("");
  const [markPaid, setMarkPaid] = useState(false);
  const [payment, setPayment] = useState<PaymentFieldsValue>(defaultPaymentFields);

  useEffect(() => {
    if (!open) return;
    setPicked(new Set(notes.map((note) => note.id)));
    setReason("");
    setMarkPaid(false);
    setPayment(defaultPaymentFields());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const pickedNotes = notes.filter((note) => picked.has(note.id));
  const anyUnpaid = pickedNotes.some((note) => note.paymentStatus !== "paid");
  const toggle = (id: number) =>
    setPicked((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const confirm = () => {
    if (!pickedNotes.length) {
      onPickNone();
      return;
    }
    onConfirm({
      ids: pickedNotes.map((note) => note.id),
      reason: reason.trim() || null,
      payment:
        markPaid && anyUnpaid
          ? {
              paidAt: payment.paidAt.toISOString(),
              method: payment.method,
              reference: payment.reference.trim() || null,
            }
          : null,
    });
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={500}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 20px)" }}
    >
      <div
        className="flex max-h-[86vh] flex-col bg-surface"
        onKeyDown={(event) => {
          if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
            event.preventDefault();
            confirm();
          }
        }}
      >
        <div className="flex flex-col gap-[3px] border-b border-shade-2 px-[22px] py-[18px]">
          <span className="text-[17px] font-semibold text-ink">{t("inventoryPage.noteCloseTitle")}</span>
          <span className="text-[13px] text-body">{t("inventoryPage.noteCloseText")}</span>
        </div>
        <div className="flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto px-[22px] py-4">
          {notes.length > 1 && (
            <div className="flex flex-col overflow-hidden rounded-lg border border-shade-2">
              {notes.map((note, index) => {
                const on = picked.has(note.id);
                return (
                  <button
                    key={note.id}
                    type="button"
                    role="checkbox"
                    aria-checked={on}
                    onClick={() => toggle(note.id)}
                    className={`flex min-h-9 w-full cursor-pointer items-center gap-2.5 border-none px-3 py-1.5 text-left text-[13px] text-ink outline-none pointer-coarse:min-h-11 focus-visible:shadow-[inset_0_0_0_2px_var(--color-brand)] ${
                      index ? "border-t border-solid border-shade-1" : ""
                    } ${on ? "bg-brand-tint" : "bg-transparent hover:bg-fill"}`}
                  >
                    <CheckBox checked={on} />
                    <span className="shrink-0 font-medium">
                      {note.invoiceNumber || t("inventoryPage.noDocNumber")}
                    </span>
                    <span className="shrink-0 text-muted">{dayjs(note.date).format("DD.MM")}</span>
                    <span className="min-w-0 flex-1 truncate text-muted" title={note.note ?? undefined}>
                      {note.note ?? ""}
                    </span>
                    <DeliveryPaymentPill delivery={note} />
                    <span className="shrink-0 font-sheet">{formatMoney(deliveryGrossOf(note))}</span>
                  </button>
                );
              })}
            </div>
          )}
          <label className="flex flex-col gap-1.5">
            <span className={PAYMENT_LABEL_CLASS}>{t("inventoryPage.noteCloseReason")}</span>
            <input
              autoFocus
              value={reason}
              maxLength={500}
              onChange={(event) => setReason(event.target.value)}
              placeholder={t("inventoryPage.noteCloseReasonPh")}
              className={`rounded-lg border border-shade-3 bg-surface px-[11px] py-[9px] text-ink outline-none focus:border-brand ${
                isNarrow ? "text-base" : "text-sm"
              }`}
            />
          </label>
          {anyUnpaid && (
            <>
              <button
                type="button"
                role="checkbox"
                aria-checked={markPaid}
                onClick={() => setMarkPaid((current) => !current)}
                className="flex min-h-9 cursor-pointer items-center gap-2.5 border-none bg-transparent p-0 text-left text-sm text-ink outline-none pointer-coarse:min-h-11"
              >
                <CheckBox checked={markPaid} />
                {t("inventoryPage.noteCloseMarkPaid", { count: pickedNotes.length })}
              </button>
              {markPaid ? (
                <PaymentFields value={payment} onChange={setPayment} />
              ) : (
                <span className="text-[12.5px] text-body">{t("inventoryPage.noteCloseStaysUnpaid")}</span>
              )}
            </>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-shade-2 px-[22px] pb-[18px] pt-3.5">
          <button type="button" onClick={onCancel} className={SECONDARY_BUTTON_CLASS}>
            {t("inventoryPage.cancel")}
          </button>
          <button type="button" disabled={saving} onClick={confirm} className={PRIMARY_BUTTON_CLASS}>
            {t("inventoryPage.noteCloseConfirm")}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default CloseWithoutInvoiceModal;
